import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/compras/pedido-compra-form-page.tsx — Fase 13, Plano 19 (COMP-02, D-14, D-20).
// Formulário "Novo pedido de compra" e "Editar pedido {número}" (`?id=`): Cabeçalho + Itens (Sheet lateral) + rodapé fixo.
// Cria por POST /api/v1/compras/pedidos e edita por PATCH /api/v1/compras/pedidos/{id} (apiMutate: sessão por cookie + CSRF).
// Na edição a `versao` lida vai no corpo (lock otimista, T-13-19D): o 409 pede "Recarregar pedido" em vez de sobrescrever.
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, MoreHorizontal, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import type { FornecedorLookup, PedidoCompraView } from '@pieltec/contracts';
import { FornecedorCombobox } from '../../components/compras/fornecedor-combobox';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { FormMessage } from '../../components/ui/form-message';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { formatarCnpj } from '../../lib/compras/cnpj';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  AJUDA_PRAZO_GERAL,
  MAXIMO_ITENS_PEDIDO,
  aplicarPrazoGeral,
  bloqueioItemComRecebimento,
  formDoPedido,
  materialSemFator,
  montarCriacaoPedido,
  montarEdicaoPedido,
  totalDoPedido,
  unidadeDeCompra,
  validarPedidoCompra,
  type ErrosPedidoCompra,
  type ItemPedidoCompraForm,
} from '../../lib/compras/pedido-form';
import { lerErroApi } from '../../lib/vendas/erro-api';
import { formatarData, formatarMoeda, formatarQuantidade, hojeIso } from '../../lib/vendas/formatacao';
import { PedidoCompraItemSheet } from './pedido-compra-item-sheet';

// O router escuta `popstate`; despachar o evento é a navegação programática sem reload (mesmo padrão do PV).
function navegar(destino: string): void {
  window.history.pushState(null, '', destino);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

const MENSAGEM_FALHA = 'Não foi possível salvar o pedido. Verifique a conexão e tente novamente.';
const MENSAGEM_RESULTADO_INCERTO_CRIACAO = 'Não foi possível confirmar a criação do pedido. Confira a lista de pedidos antes de criar de novo, para não duplicar.';
const CODIGO_VERSAO_DESATUALIZADA = 'PEDIDO_COMPRA_VERSAO_DESATUALIZADA';
const CABECALHO_COLUNA = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

function focarCampo(id: string): void {
  window.requestAnimationFrame(() => document.getElementById(id)?.focus());
}

export function PedidoCompraFormPage() {
  const parametros = new URLSearchParams(window.location.search);
  const pedidoId = parametros.get('id');
  const itemDaUrl = useRef(parametros.get('item'));
  const edicao = pedidoId !== null;

  const [original, setOriginal] = useState<PedidoCompraView | null>(null);
  const [carregandoPedido, setCarregandoPedido] = useState(edicao);
  const [erroCarga, setErroCarga] = useState<'carga' | 'nao-encontrado' | null>(null);
  const [fornecedor, setFornecedor] = useState<FornecedorLookup | null>(null);
  const [data, setData] = useState(hojeIso);
  const [prazoGeral, setPrazoGeral] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<ItemPedidoCompraForm[]>([]);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [indiceEdicao, setIndiceEdicao] = useState<number | null>(null);
  const [erros, setErros] = useState<ErrosPedidoCompra>({});
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [versaoDesatualizada, setVersaoDesatualizada] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  /** Carrega o pedido para edição e substitui o estado do formulário (também é o "Recarregar pedido" do 409). */
  const carregarPedido = useCallback(async (): Promise<ItemPedidoCompraForm[] | null> => {
    if (!pedidoId) return null;
    setCarregandoPedido(true);
    setErroCarga(null);
    try {
      const res = await fetch(`/api/v1/compras/pedidos/${encodeURIComponent(pedidoId)}`, { credentials: 'include' });
      if (!montado.current) return null;
      if (res.status === 404 || res.status === 400) {
        setErroCarga('nao-encontrado');
        return null;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const pedido = (await res.json()) as PedidoCompraView;
      if (!montado.current) return null;
      const estado = formDoPedido(pedido);
      setOriginal(pedido);
      setData(estado.cabecalho.data);
      setPrazoGeral(estado.cabecalho.prazoGeral);
      setObservacoes(estado.cabecalho.observacoes);
      setItens(estado.itens);
      setErros({});
      setErroEnvio(null);
      setVersaoDesatualizada(false);
      return estado.itens;
    } catch {
      if (montado.current) setErroCarga('carga');
      return null;
    } finally {
      if (montado.current) setCarregandoPedido(false);
    }
  }, [pedidoId]);

  useEffect(() => {
    void carregarPedido().then((carregados) => {
      // "Editar item" do detalhe chega com `&item=` e já abre o Sheet daquele item.
      if (!carregados || !itemDaUrl.current) return;
      const alvo = itemDaUrl.current;
      itemDaUrl.current = null;
      const indice = carregados.findIndex((item) => item.id === alvo && !item.somenteLeitura);
      if (indice >= 0) {
        setIndiceEdicao(indice);
        setSheetAberto(true);
      }
    });
  }, [carregarPedido]);

  function abrirNovoItem(): void {
    setIndiceEdicao(null);
    setSheetAberto(true);
  }

  function editarItem(indice: number): void {
    setIndiceEdicao(indice);
    setSheetAberto(true);
  }

  function salvarItem(item: ItemPedidoCompraForm, continuar: boolean): void {
    // O item NOVO herda o prazo geral quando não tem prazo próprio (nunca sobrescreve um prazo informado).
    setItens((atuais) =>
      indiceEdicao === null
        ? [...atuais, ...aplicarPrazoGeral([item], prazoGeral)]
        : atuais.map((existente, i) => (i === indiceEdicao ? item : existente)),
    );
    setErros((atuais) => ({ ...atuais, itens: undefined }));
    if (!continuar) setSheetAberto(false);
  }

  /** Remove o item; na edição, item com recebimento não sai (mesma regra e mensagem do servidor, ITEM_COM_RECEBIMENTO). */
  function removerItem(indice: number): void {
    const item = itens[indice];
    if (!item) return;
    const bloqueio = item.id ? bloqueioItemComRecebimento(indice + 1, item, null) : null;
    if (bloqueio) {
      setErros((atuais) => ({ ...atuais, itens: bloqueio }));
      return;
    }
    setItens((atuais) => atuais.filter((_, j) => j !== indice));
  }

  // O prazo geral preenche só os itens sem prazo; um prazo já ajustado item a item nunca é sobrescrito (D-20).
  // Itens encerrados/cancelados (somente leitura) ficam como estão.
  function alterarPrazoGeral(valor: string): void {
    setPrazoGeral(valor);
    setItens((atuais) => atuais.map((item) => (item.somenteLeitura ? item : (aplicarPrazoGeral([item], valor)[0] ?? item))));
  }

  function validar(): boolean {
    const proximos = validarPedidoCompra({ fornecedorId: edicao ? original?.fornecedorId : fornecedor?.id, totalItens: itens.length });
    setErros(proximos);
    if (proximos.fornecedorId) focarCampo('pc-fornecedor');
    else if (proximos.itens) focarCampo('pc-adicionar-item');
    return !proximos.fornecedorId && !proximos.itens;
  }

  async function confirmar(): Promise<void> {
    setErroEnvio(null);
    if (!validar()) return;
    setEnviando(true);
    try {
      const res =
        edicao && original
          ? await apiMutate(`/api/v1/compras/pedidos/${encodeURIComponent(original.id)}`, {
              method: 'PATCH',
              body: montarEdicaoPedido(original.versao, { prazoGeral, observacoes }, itens),
            })
          : fornecedor
            ? await apiMutate('/api/v1/compras/pedidos', {
                body: montarCriacaoPedido({ fornecedorId: fornecedor.id, data, prazoGeral, observacoes }, itens),
              })
            : null;
      if (!res) return;
      if (!res.ok) {
        const falha = await lerErroApi(res, MENSAGEM_FALHA);
        if (res.status === 409 && falha.codigo === CODIGO_VERSAO_DESATUALIZADA) {
          setVersaoDesatualizada(true);
          return;
        }
        setErroEnvio(falha.mensagem);
        return;
      }
      const salvo = (await res.json()) as PedidoCompraView;
      // `criado=1` faz o detalhe mostrar "Pedido {número} criado." com o atalho "Baixar PDF do pedido" (D-14).
      navegar(`/compras/pedidos/detalhe?id=${encodeURIComponent(salvo.id)}${edicao ? '' : '&criado=1'}`);
    } catch {
      // A criação ainda não aceita chave de idempotência no servidor: após queda de rede o pedido pode ter sido gravado.
      // A edição é protegida pela versão (lock otimista), então só a criação tem resultado incerto.
      setErroEnvio(edicao ? MENSAGEM_FALHA : MENSAGEM_RESULTADO_INCERTO_CRIACAO);
    } finally {
      if (montado.current) setEnviando(false);
    }
  }

  if (edicao && carregandoPedido && !original) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-6" aria-busy="true" aria-label="Carregando pedido de compra">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (edicao && (erroCarga || !original)) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">
              {erroCarga === 'nao-encontrado'
                ? 'Pedido de compra não encontrado.'
                : 'Não foi possível carregar o pedido de compra. Verifique a conexão e tente novamente.'}
            </p>
            {erroCarga === 'carga' ? (
              <Button type="button" variant="outline" onClick={() => void carregarPedido()}>
                <RefreshCw aria-hidden="true" />
                Tentar novamente
              </Button>
            ) : (
              <Button asChild variant="outline">
                <a href="/compras/pedidos">Voltar</a>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (original?.cancelado) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Este pedido está cancelado e não pode ser editado.</p>
            <Button asChild variant="outline">
              <a href={`/compras/pedidos/detalhe?id=${encodeURIComponent(original.id)}`}>Voltar ao pedido</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const total = totalDoPedido(itens);
  const voltarPara = edicao && original ? `/compras/pedidos/detalhe?id=${encodeURIComponent(original.id)}` : '/compras/pedidos';

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col">
      <div className="flex flex-col gap-6 p-6">
        <h1 className="text-xl font-semibold leading-tight">{original ? `Editar pedido ${original.numero}` : 'Novo pedido de compra'}</h1>

        {versaoDesatualizada && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            <span className="font-medium">Este pedido foi alterado por outro usuário. Recarregue para ver as mudanças antes de salvar.</span>
            <Button type="button" variant="outline" size="sm" disabled={carregandoPedido} onClick={() => void carregarPedido()}>
              <RefreshCw aria-hidden="true" />
              Recarregar pedido
            </Button>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-semibold uppercase tracking-wider">Cabeçalho</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pc-fornecedor">Fornecedor *</Label>
              {original ? (
                <div id="pc-fornecedor" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  {original.fornecedorRazaoSocial}
                  <span className="ml-2 text-xs text-muted-foreground tabular-nums">{formatarCnpj(original.fornecedorCnpj)}</span>
                </div>
              ) : (
                <FornecedorCombobox
                  id="pc-fornecedor"
                  value={fornecedor}
                  onChange={(escolhido) => {
                    setFornecedor(escolhido);
                    setErros((atuais) => ({ ...atuais, fornecedorId: undefined }));
                  }}
                  incluirAConfirmar={false}
                  invalido={!!erros.fornecedorId}
                />
              )}
              {erros.fornecedorId && <FormMessage>{erros.fornecedorId}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pc-data">Data do pedido</Label>
              <DatePicker id="pc-data" value={data} onValueChange={(value) => setData(value)} disabled={edicao} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pc-prazo-geral">Prazo geral de entrega</Label>
              <DatePicker
                id="pc-prazo-geral"
                value={prazoGeral}
                onValueChange={(value) => alterarPrazoGeral(value)}
                aria-describedby="pc-prazo-geral-ajuda"
              />
              <p id="pc-prazo-geral-ajuda" className="text-sm text-muted-foreground">
                {AJUDA_PRAZO_GERAL}
              </p>
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <Label htmlFor="pc-observacoes">Observações</Label>
              <Textarea id="pc-observacoes" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} maxLength={2000} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider">Itens</CardTitle>
            <Button id="pc-adicionar-item" type="button" variant="outline" onClick={abrirNovoItem} disabled={itens.length >= MAXIMO_ITENS_PEDIDO}>
              <Plus aria-hidden="true" />
              Adicionar item
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {itens.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <p className="text-center text-sm text-muted-foreground">Nenhum item neste pedido.</p>
                <Button type="button" variant="outline" onClick={abrirNovoItem}>
                  <Plus aria-hidden="true" />
                  Adicionar item
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Itens do pedido de compra</caption>
                  <thead className="bg-slate-100 text-left">
                    <tr>
                      <th scope="col" className={CABECALHO_COLUNA}>Matéria-prima</th>
                      <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Qtd.</th>
                      <th scope="col" className={CABECALHO_COLUNA}>Un.</th>
                      <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Preço</th>
                      <th scope="col" className={CABECALHO_COLUNA}>Prazo</th>
                      <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Total</th>
                      <th scope="col" className="px-3 py-2">
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((item, i) => (
                      <tr key={item.id ?? `${item.material.id}-${i}`} className={`border-t ${item.somenteLeitura ? 'text-muted-foreground' : ''}`}>
                        <td className="max-w-[22rem] px-3 py-2">
                          <div className="truncate font-semibold" title={item.material.nome}>
                            {item.material.codigo}
                          </div>
                          <div className="truncate text-muted-foreground">{item.material.nome}</div>
                          {materialSemFator(item.material) && (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                              <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
                              Sem fator de conversão
                            </span>
                          )}
                          {item.somenteLeitura && <span className="mt-1 block text-xs">Item encerrado: sem alterações.</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatarQuantidade(item.quantidade)}</td>
                        <td className="px-3 py-2">{unidadeDeCompra(item.material)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(item.precoUnitario, 4)}</td>
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatarData(item.prazoEntrega)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(totalDoPedido([item]))}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          {!item.somenteLeitura && (
                            <>
                              <Button type="button" variant="ghost" size="sm" aria-label={`Editar item ${i + 1}`} onClick={() => editarItem(i)}>
                                <Pencil aria-hidden="true" />
                                Editar
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button type="button" variant="ghost" size="sm" aria-label={`Mais ações do item ${i + 1}`}>
                                    <MoreHorizontal aria-hidden="true" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onSelect={() => removerItem(i)}>
                                    <Trash2 aria-hidden="true" />
                                    Remover item
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {erros.itens && <FormMessage>{erros.itens}</FormMessage>}
            <p className="text-right text-base font-semibold tabular-nums" aria-live="polite">
              Total do pedido: {formatarMoeda(total)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-0 border-t bg-white px-6 py-3">
        {erroEnvio && <FormMessage className="mb-2">{erroEnvio}</FormMessage>}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {edicao ? <span /> : <p className="text-sm text-muted-foreground">O pedido é criado já em aberto; não há aprovação.</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navegar(voltarPara)} disabled={enviando}>
              Voltar
            </Button>
            <Button type="button" onClick={() => void confirmar()} disabled={enviando || versaoDesatualizada}>
              {edicao ? 'Salvar pedido' : 'Confirmar pedido'}
            </Button>
          </div>
        </div>
      </div>

      <PedidoCompraItemSheet
        aberto={sheetAberto}
        onAbertoChange={setSheetAberto}
        fornecedorId={original?.fornecedorId ?? fornecedor?.id ?? null}
        inicial={indiceEdicao === null ? null : (itens[indiceEdicao] ?? null)}
        numeroItem={indiceEdicao === null ? itens.length + 1 : indiceEdicao + 1}
        prazoGeral={prazoGeral}
        onSalvar={salvarItem}
      />
    </div>
  );
}
